import React, { useState, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Upload as UploadIcon, FileSpreadsheet, CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Columns to auto-skip during upload (case-insensitive partial matching)
const COLUMNS_TO_SKIP = [
  'patient last',
  'patient first',
  'medical record',
  'branch name',
  'phone',
  'time in',
  'time out',
  'documentation time',
  'travel time',
  'productivity units',
  'mileage',
  'employee id'
];

export default function UploadProjections() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [projectionType, setProjectionType] = useState('Proj 1');
  const [showMapping, setShowMapping] = useState(false);
  const [columns, setColumns] = useState([]);
  const [mappings, setMappings] = useState({});
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (
        selectedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        selectedFile.type === 'application/vnd.ms-excel'
      ) {
        setFile(selectedFile);
        setUploadResult(null);
        readExcelColumns(selectedFile);
      } else {
        toast.error('Please select a valid Excel file (.xlsx or .xls)');
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (
        droppedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        droppedFile.type === 'application/vnd.ms-excel'
      ) {
        setFile(droppedFile);
        setUploadResult(null);
        readExcelColumns(droppedFile);
      } else {
        toast.error('Please select a valid Excel file (.xlsx or .xls)');
      }
    }
  };

  const readExcelColumns = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
        
        if (jsonData.length > 0) {
          const headers = jsonData[0];
          setColumns(headers);
          
          // Auto-map common column names - with extensive pattern matching
          const autoMappings = {};
          console.log('=== COLUMN AUTO-MAPPING DEBUG ===');
          console.log('Headers found:', headers);
          
          headers.forEach((header) => {
            const lowerHeader = String(header).toLowerCase().trim();
            const normalized = lowerHeader.replace(/[_\s]+/g, ' ');
            let mapping = 'additional';
            
            // Check if this column should be auto-skipped
            const shouldSkip = COLUMNS_TO_SKIP.some(skipCol => 
              normalized.startsWith(skipCol) || normalized === skipCol
            );
            
            if (shouldSkip) {
              mapping = 'skip';
            }
            // First Name patterns: "Provider First", "First Name", "First", "Therapist First Name", etc.
            // But NOT "Patient First Name"
            else if (lowerHeader.includes('first') && !lowerHeader.includes('patient') && (
                lowerHeader.includes('name') || 
                lowerHeader.includes('provider') || 
                lowerHeader.includes('therapist') ||
                lowerHeader === 'first'
            )) {
              mapping = 'first_name';
            }
            // Last Name patterns: "Provider Last", "Last Name", "Last", "Therapist Last Name", etc.
            // But NOT "Patient Last Name"
            else if (lowerHeader.includes('last') && !lowerHeader.includes('patient') && (
                lowerHeader.includes('name') || 
                lowerHeader.includes('provider') || 
                lowerHeader.includes('therapist') ||
                lowerHeader === 'last'
            )) {
              mapping = 'last_name';
            }
            // Date patterns
            else if (lowerHeader.includes('date') || lowerHeader.includes('target') || lowerHeader.includes('scheduled')) {
              mapping = 'date';
            }
            // Task/Visit name patterns
            else if (lowerHeader.includes('task name') || lowerHeader.includes('visit name') || 
                     lowerHeader === 'task' || lowerHeader === 'visit' ||
                     (lowerHeader.includes('visit') && lowerHeader.includes('type')) ||
                     (lowerHeader.includes('task') && !lowerHeader.includes('type') && !lowerHeader.includes('category'))) {
              mapping = 'task_name';
            }
            // Task type/category patterns  
            else if (lowerHeader.includes('task type') || lowerHeader.includes('type') || 
                     lowerHeader.includes('category')) {
              mapping = 'task_type';
            }
            // Status patterns
            else if (lowerHeader.includes('status')) {
              mapping = 'status';
            }
            // Address patterns
            else if (lowerHeader.includes('address')) {
              mapping = 'address';
            }
            // City patterns
            else if (lowerHeader.includes('city')) {
              mapping = 'city';
            }
            // State patterns (but not "status")
            else if (lowerHeader.includes('state') && !lowerHeader.includes('status')) {
              mapping = 'state';
            }
            // ZIP patterns
            else if (lowerHeader.includes('zip')) {
              mapping = 'zip_code';
            }
            // Agency/Insurance patterns
            else if (lowerHeader.includes('agency') || lowerHeader.includes('insurance') || 
                     lowerHeader.includes('payer') || lowerHeader.includes('company')) {
              mapping = 'agency';
            }
            
            autoMappings[header] = mapping;
            console.log(`Column "${header}" -> ${mapping}`);
          });
          
          setMappings(autoMappings);
          setShowMapping(true);
        }
      } catch (error) {
        toast.error('Failed to read Excel file');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    // Check required mappings
    const hasFirstName = Object.values(mappings).includes('first_name');
    const hasLastName = Object.values(mappings).includes('last_name');
    
    if (!hasFirstName || !hasLastName) {
      toast.error('Please map First Name and Last Name columns');
      return;
    }

    setUploading(true);
    
    // Capture current state values to use in async callback
    const currentMappings = { ...mappings };
    const currentProjectionType = projectionType;
    const currentFile = file;
    
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });
          
          console.log('=== PROJECTION UPLOAD DEBUG ===');
          console.log('Total rows parsed from Excel:', jsonData.length);
          console.log('First row keys:', jsonData.length > 0 ? Object.keys(jsonData[0]) : 'none');
          console.log('Current mappings:', currentMappings);
          
          // Calculate the Saturday of current week for week_ending.
          // Use LOCAL date parts (not toISOString, which converts to UTC and can roll an
          // evening upload's Saturday into Sunday → backend then normalizes to the WRONG week).
          const now = new Date();
          const saturday = new Date(now);
          saturday.setDate(now.getDate() + (6 - now.getDay()));
          const weekEnding = `${saturday.getFullYear()}-${String(saturday.getMonth() + 1).padStart(2, '0')}-${String(saturday.getDate()).padStart(2, '0')}`;
          
          // Transform data according to mappings - preserve ALL rows as individual visits
          const allVisitRows = [];
          let skippedRows = 0;
          
          jsonData.forEach((row, index) => {
            const mappedRow = {};
            Object.entries(row).forEach(([key, value]) => {
              const mapping = currentMappings[key];
              if (mapping && mapping !== 'skip' && mapping !== 'additional') {
                mappedRow[mapping] = value;
              }
            });
            
            const firstName = String(mappedRow.first_name || '').trim();
            const lastName = String(mappedRow.last_name || '').trim();
            const therapistName = `${firstName} ${lastName}`.trim();
            
            if (!therapistName) {
              skippedRows++;
              if (skippedRows <= 5) {
                console.log(`Row ${index + 1} skipped - no therapist name. Original keys:`, Object.keys(row));
              }
              return;
            }
            
            allVisitRows.push({
              therapistName,
              agency: String(mappedRow.agency || '').trim() || null,
              task_name: mappedRow.task_name || '',
              task_type: mappedRow.task_type || '',
              date: mappedRow.date || '',
              status: mappedRow.status || '',
            });
          });
          
          console.log('Total rows with valid therapist names:', allVisitRows.length);
          console.log('Skipped rows (no name):', skippedRows);
          
          // Group by therapist (and optionally agency)
          const groupedData = {};
          allVisitRows.forEach(item => {
            const key = `${item.therapistName}_${item.agency || ''}`;
            
            if (!groupedData[key]) {
              groupedData[key] = {
                therapist_name: item.therapistName,
                week_ending: weekEnding,
                agency: item.agency,
                projected_count: 0,
                visit_details: []
              };
            }
            
            groupedData[key].projected_count += 1;
            
            // Always add visit details regardless of task_name presence
            groupedData[key].visit_details.push({
              visit_name: item.task_name,
              task_type: item.task_type,
              date: item.date,
              status: item.status,
              agency: item.agency,  // Include agency in visit details too
            });
          });
          
          const finalData = Object.values(groupedData);
          
          console.log('Unique therapist groups:', finalData.length);
          console.log('Total visit_details across all groups:', finalData.reduce((sum, g) => sum + g.visit_details.length, 0));
          
          const response = await axios.post(`${API}/projections/upload`, {
            data: finalData,
            projection_type: currentProjectionType
          });
          
          setUploadResult(response.data);
          toast.success(`${currentProjectionType} uploaded successfully!`);
          setFile(null);
          setShowMapping(false);
          setColumns([]);
          setMappings({});
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        } catch (error) {
          console.error('Upload error:', error);
          toast.error(error.response?.data?.detail || 'Upload failed');
        } finally {
          setUploading(false);
        }
      };
      reader.onerror = () => {
        toast.error('Failed to read file');
        setUploading(false);
      };
      reader.readAsArrayBuffer(currentFile);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed');
      setUploading(false);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div>
        <h1 className="font-heading text-4xl font-semibold text-foreground tracking-tight">
          Upload Projections
        </h1>
        <p className="text-muted-foreground mt-2 leading-relaxed">
          Upload projected visit data for Proj 1 (Monday) or Proj 2 (Thursday)
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-8">
          <h3 className="font-heading text-xl font-semibold mb-6">Upload Projection File</h3>
          
          {!showMapping ? (
            <>
              <div className="mb-6">
                <Label>Projection Type</Label>
                <Select value={projectionType} onValueChange={setProjectionType}>
                  <SelectTrigger className="mt-2" data-testid="projection-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Proj 1">Proj 1 (Monday)</SelectItem>
                    <SelectItem value="Proj 2">Proj 2 (Thursday)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div
                className={`drag-drop-zone rounded-xl p-12 text-center cursor-pointer ${
                  dragOver ? 'drag-over' : ''
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-dropzone"
              >
                <FileSpreadsheet className="mx-auto mb-4 text-primary" size={48} strokeWidth={1.5} />
                <p className="text-foreground font-medium mb-2">
                  Drag and drop your Excel file here
                </p>
                <p className="text-sm text-muted-foreground mb-4">or click to browse</p>
                <p className="text-xs text-muted-foreground">
                  Supported formats: .xlsx, .xls
                </p>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
                data-testid="file-input"
              />

              {file && !showMapping && (
                <div className="mt-6 p-4 bg-muted rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileSpreadsheet className="text-primary" size={24} />
                    <div>
                      <p className="font-medium text-sm">{file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(file.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="font-medium text-sm">{file.name}</p>
                  <p className="text-xs text-muted-foreground">Map your columns for {projectionType}</p>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowMapping(false);
                    setFile(null);
                    setColumns([]);
                    setMappings({});
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  data-testid="cancel-mapping"
                >
                  Cancel
                </Button>
              </div>

              <div className="max-h-96 overflow-y-auto space-y-3 border border-border rounded-lg p-4">
                {columns.map((column, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="flex-1">
                      <Label className="text-xs text-muted-foreground">Excel Column</Label>
                      <p className="font-medium text-sm mt-1">{column}</p>
                    </div>
                    <ArrowRight className="text-muted-foreground flex-shrink-0" size={20} />
                    <div className="flex-1">
                      <Label className="text-xs text-muted-foreground">Map To</Label>
                      <Select
                        value={mappings[column] || 'additional'}
                        onValueChange={(value) => 
                          setMappings({ ...mappings, [column]: value })
                        }
                      >
                        <SelectTrigger className="mt-1" data-testid={`map-${column}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="first_name">First Name</SelectItem>
                          <SelectItem value="last_name">Last Name</SelectItem>
                          <SelectItem value="date">Date</SelectItem>
                          <SelectItem value="task_name">Task Name</SelectItem>
                          <SelectItem value="task_type">Task Type</SelectItem>
                          <SelectItem value="status">Status</SelectItem>
                          <SelectItem value="address">Address</SelectItem>
                          <SelectItem value="city">City</SelectItem>
                          <SelectItem value="state">State</SelectItem>
                          <SelectItem value="zip_code">ZIP Code</SelectItem>
                          <SelectItem value="agency">Agency</SelectItem>
                          <SelectItem value="additional">Additional Data</SelectItem>
                          <SelectItem value="skip">Skip Column</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  {Object.values(mappings).filter(v => v === 'first_name').length > 0 && 
                   Object.values(mappings).filter(v => v === 'last_name').length > 0 ? (
                    <span className="text-sage flex items-center gap-2">
                      <CheckCircle2 size={16} />
                      Required fields mapped
                    </span>
                  ) : (
                    <span className="text-clay flex items-center gap-2">
                      <AlertCircle size={16} />
                      Map First Name and Last Name
                    </span>
                  )}
                </div>
                <Button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="gap-2"
                  data-testid="upload-button"
                >
                  <UploadIcon size={16} />
                  {uploading ? 'Uploading...' : 'Upload Projections'}
                </Button>
              </div>
            </div>
          )}
        </Card>

        <Card className="p-8">
          <h3 className="font-heading text-xl font-semibold mb-6">Excel Format Guidelines</h3>
          <div className="space-y-4">
            <div className="flex gap-3">
              <CheckCircle2 className="text-primary flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="font-medium text-sm mb-1">Required Columns</p>
                <p className="text-sm text-muted-foreground">
                  First Name, Last Name
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <CheckCircle2 className="text-primary flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="font-medium text-sm mb-1">Optional Columns</p>
                <p className="text-sm text-muted-foreground">
                  Task Name (or Visit Name), Task Type, Date
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <AlertCircle className="text-clay flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="font-medium text-sm mb-1">Important Notes</p>
                <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                  <li>Select Proj 1 for Monday uploads</li>
                  <li>Select Proj 2 for Thursday uploads</li>
                  <li>Week automatically set to current/upcoming Saturday</li>
                  <li>Each row counts as 1 projected visit</li>
                  <li>Task Name used for Eval/Discharge tracking</li>
                </ul>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {uploadResult && (
        <Card className="p-6 bg-sage/5 border-sage/20">
          <div className="flex items-start gap-4">
            <CheckCircle2 className="text-sage flex-shrink-0 mt-1" size={24} />
            <div className="flex-1">
              <h3 className="font-heading text-lg font-semibold text-sage mb-3">
                Upload Successful!
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-sm text-muted-foreground">Therapist Groups Added</p>
                  <p className="text-2xl font-bold text-foreground tabular-nums">
                    {uploadResult.projections_added}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Therapist Groups Updated</p>
                  <p className="text-2xl font-bold text-foreground tabular-nums">
                    {uploadResult.projections_updated}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Visits Processed</p>
                  <p className="text-2xl font-bold text-foreground tabular-nums">
                    {uploadResult.total_visits_processed || 'N/A'}
                  </p>
                </div>
                {uploadResult.personnel_created > 0 && (
                  <div>
                    <p className="text-sm text-muted-foreground">New Therapists</p>
                    <p className="text-2xl font-bold text-primary tabular-nums">
                      {uploadResult.personnel_created}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Agency Breakdown */}
              {uploadResult.agency_breakdown && Object.keys(uploadResult.agency_breakdown).length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                    <FileSpreadsheet size={16} className="text-primary" />
                    Agency Breakdown (for debugging)
                  </h4>
                  <div className="max-h-64 overflow-y-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50 sticky top-0">
                        <tr>
                          <th className="text-left p-2 font-medium">Agency</th>
                          <th className="text-center p-2 font-medium">Raw Count</th>
                          <th className="text-center p-2 font-medium">Weighted</th>
                          <th className="text-left p-2 font-medium">Visit Types</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(uploadResult.agency_breakdown)
                          .sort((a, b) => b[1].weighted_count - a[1].weighted_count)
                          .map(([agency, data]) => (
                          <tr key={agency} className="border-t hover:bg-muted/30">
                            <td className="p-2 font-medium">{agency}</td>
                            <td className="p-2 text-center">{data.count}</td>
                            <td className="p-2 text-center font-bold text-primary">{data.weighted_count}</td>
                            <td className="p-2">
                              <div className="flex flex-wrap gap-1">
                                {Object.entries(data.visit_types || {}).map(([type, info]) => (
                                  <span 
                                    key={type} 
                                    className="inline-flex items-center px-2 py-0.5 bg-muted rounded text-xs"
                                    title={`${info.count} visits, ${info.weighted} weighted`}
                                  >
                                    {type}: {info.count} ({info.weighted}w)
                                  </span>
                                ))}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    * "Weighted" applies 2x multiplier for billable OASIS visits. Use this to compare against your expected totals.
                  </p>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
